import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Axios from 'axios';

import ls from 'local-storage';
import styled from 'styled-components';

import { getMoment, getNow, toISODate } from '@libs/NwMoment';

import { useLayoutContext } from '@contexts/LayoutContext';
import { useUserPermission } from '@contexts/UserContext';

import { useContact } from '@hooks/API/Contact/UseContact';
import { getModel, isContactApproved } from '@hooks/API/Contact/UseContactsList';

//import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import AddEventsButtons from '@components/AddEventsButtons/AddEventsButtons';
import EventDrawer from '@components/EventDrawer/EventDrawer';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
//import ModelCalendar from '@components/Schedule/Calendar/ModelCalendar/ModelCalendar';
import { NwButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwIcon from '@components/Gui/NwIcon';
import AgendaContainer from '@components/Schedule/Agenda/AgendaContainer';
import CalendarCardWrapper from '@components/Schedule/Calendar/CalendarCardUI';
import CalendarFooter from '@components/Schedule/Calendar/CalendarFooter';
import AvailableEventsDrawer from '@components/Schedule/Calendar/ModelCalendar/AvailableEventsDrawer';
import ExportListDrawer from '@components/Schedule/ExportListDrawer/ExportListDrawer';
import { getCalendarDefaultDate, getWeekRangeDates } from '@components/Schedule/ScheduleUtils';
import SendAgendaDrawer from '@components/SendAgendaDrawer/SendAgendaDrawer';

import { AgendaProvider } from './Agenda/AgendaContext';
import AgendaFooter from './Agenda/AgendaFooter';
import MobileAgendaContainer from './Agenda/MobileAgendaContainer';
import ModelCalendar from './Calendar/ModelCalendar/ModelCalendar';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const LockContainer = styled(FlexContainer)`
    min-height: 320px;
    padding: 16px 0;
    svg {
        font-size: ${(props) => (props.$previewMode ? '80px' : '120px')};
    }
`;

const ScheduleMobileWrapper = styled.div`
    min-height: 100vh;
    padding: 0.25rem;
    background: white;
`;

const ContactScheduleCardWrapper = styled(CalendarCardWrapper)`
    ${(props) =>
        props.$stretchAgendaContent &&
        `
        .ant-card-body {
            min-height: 0;
        }

        .ant-card-body-content {
            flex: 1 1 auto;
            min-height: 0;
            align-items: stretch;
        }
    `}
`;

const ContactScheduleCard = ({
    bookingSection,
    calendarStyle,
    contactId,
    filters,
    isMobileDrawer,
    onClearContactID,
    onScheduleRangeChange,
    previewMode,
    scheduleRange,
    setFilters,
    stretchToParent = false,
    // setTimeGrid,
    // timeGrid,
    type,
}) => {
    const isApproved = isContactApproved(contactId);

    const navigate = useNavigate();
    const [isFetchingCastings, setIsFetchingCastings] = useState(false);
    const [dateEvents, setDateEvents] = useState([]);
    const [dateSelectedEvents, setDateSelectedEvents] = useState([]);
    const [sendAgenda, setSendAgenda] = useState(false);
    const [exportList, setExportList] = useState(false);
    const [reload, setReload] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isEventDrawer, setIsEventDrawer] = useState(false);
    const [newEventType, setNewEventType] = useState();
    const [newEventDate, setNewEventDate] = useState(); //date selected for the new event
    const [shareRangeDates, setShareRangeDates] = useState(scheduleRange);
    const [availableEventsDrawerDate, setAvailableEventsDrawerDate] = useState(null);

    const [params, setParams] = useState({
        inclJobs: true,
        inclExtJobs: true,
        inclCastings: true,
        checkConflict: false,
        inclTravels: true,
        inclAccommodations: true,
        inclExternalJobs: true,
        inclFollowUps: true,
        inclMeetings: true,
        inclNotices: true,
        General: false,
        Warning: false,
        idmodels: [contactId],
    });

    const { isCalendarMobile } = useLayoutContext();
    const layoutContext = useLayoutContext();
    const { hasCollapsibleSidebar } = layoutContext;
    const currentContact = getModel(contactId);
    const viewModelCalendar = useUserPermission(
        'ViewModelCalendar',
        currentContact && currentContact.U.map((user) => user.UserID)
    );
    const viewCalendar = type === 'model' ? viewModelCalendar : true;

    useEffect(() => {
        if (previewMode) {
            setFilters({
                filterMine: 'all',
                filterAcknowledgement: 'all',
                hideCancelled: false,
                hideBilling: false,
            });
        }
    }, []);

    useEffect(() => {
        setParams({
            ...params,
            idmodels: [contactId],
        });
    }, [contactId]);

    useEffect(() => {
        const setEvent = async () => {
            await setSelectedEvent(ls.get('selectedEvent'));
            ls.remove('selectedEvent');
        };
        if (ls.get('selectedEvent')) {
            setEvent();
        }
    }, []);

    useEffect(() => {
        setDateEvents([]);
    }, [scheduleRange]);

    const availableEvents = useMemo(() => {
        const events = [];
        if (dateEvents) {
            dateEvents.map((event) => {
                if (!dateSelectedEvents.find((selectedEvent) => selectedEvent.ID === event.ID)) {
                    events.push({
                        ...event,
                        isAvailable: true,
                    });
                }
                return null;
            });
        }
        return events;
    }, [dateSelectedEvents, dateEvents]);

    const displayedEvents = [...dateSelectedEvents, ...availableEvents];
    displayedEvents.sort((event1, event2) => new Date(event1.FromDate) - new Date(event2.ToDate));

    const handleCreateEvent = (type, date) => {
        setNewEventType(type);
        setNewEventDate(date);
        setIsEventDrawer(true);
    };

    const onOpenSendAgendaDrawer = () => {
        setShareRangeDates({ ...scheduleRange });
        setSendAgenda(true);
    };

    const onOpenExportListDrawer = () => {
        setShareRangeDates({ ...scheduleRange });
        setExportList(true);
    };

    const onOpenSendAgendaDrawerOnSpecificDate = (date) => {
        setShareRangeDates({ start: getMoment(date), end: getMoment(date) });
        setSendAgenda(true);
    };

    const onOpenAvailableEventsDrawer = (date) => {
        setAvailableEventsDrawerDate(date);
    };

    const onCloseSendAgendaDrawer = () => {
        setShareRangeDates({ ...scheduleRange });
        setReload(true);
        setSendAgenda(false);
    };

    const onCloseExportListDrawer = () => {
        setShareRangeDates({ ...scheduleRange });
        setExportList(false);
    };

    const handleReload = () => {
        setReload(true);
    };

    const handleReloadComplete = () => {
        setReload(false);
    };

    const handleCloseEventDrawer = () => {
        setSelectedEvent(null);
        setReload(true);
    };

    const handleCloseEventDrawerNoReload = () => {
        setSelectedEvent(null);
    };

    const goToModelSchedule = () => {
        if (scheduleRange && scheduleRange.start) {
            const startIsoNoTime = toISODate(scheduleRange.start);
            if (startIsoNoTime) {
                const weekRangeDates = getWeekRangeDates(scheduleRange.start);
                ls.set('nw_config_booking_schedule_start_range', toISODate(weekRangeDates.start));
                ls.set('nw_config_booking_schedule_end_range', toISODate(weekRangeDates.end));
                navigate(`/schedule/all/all/${contactId}`);
            }
        }
    };

    const getCardMinHeight = () => {
        if (bookingSection) {
            return 'calc( 100vh - 10rem)';
        } else {
            if (calendarStyle !== 'agenda') {
                return 'calc(100vh - 20rem)';
            } else {
                if (type === 'model') {
                    return 580;
                }
                return 520;
            }
        }
    };

    const handleSelectEvent = useCallback((eventdata) => {
        setSelectedEvent(eventdata);
    }, []);

    const [forceReload, setForceReload] = useState(false);

    const handleUpdateFromEventDrawer = () => {
        setForceReload(true);
    };

    const afterReload = useCallback(() => {
        setForceReload(false);
    }, []);

    const handleClearContactId = useCallback(() => {
        onClearContactID();
    }, []);

    const AgendaContextValues = useMemo(
        () => ({
            afterReload,
            bookingSection,
            contactId,
            forceReload,
            isMobileDrawer,
            onClearContactID: handleClearContactId,
            onScheduleRangeChange,
            onSelectEvent: handleSelectEvent,
            params,
            previewMode,
            scheduleRange,
            selectedEvent,
            type,
        }),
        [
            afterReload,
            bookingSection,
            contactId,
            forceReload,
            handleClearContactId,
            handleSelectEvent,
            isMobileDrawer,
            onScheduleRangeChange,
            params,
            previewMode,
            scheduleRange,
            selectedEvent,
            type,
        ]
    );

    const calendarDefaultDate = scheduleRange ? getCalendarDefaultDate(scheduleRange) : getNow();
    const isAgenda = calendarStyle === 'agenda';

    return (
        <>
            <ContactScheduleCardWrapper
                $hasSidebar={!hasCollapsibleSidebar}
                $bookingSection={bookingSection}
                $stretchToParent={stretchToParent}
                $stretchAgendaContent={stretchToParent && isAgenda}
                className={calendarStyle !== 'agenda' ? `chart-${calendarStyle}` : 'agenda'}
            >
                {isCalendarMobile ? (
                    <ScheduleMobileWrapper>
                        {scheduleRange ? (
                            <AgendaProvider values={AgendaContextValues}>
                                <MobileAgendaContainer />
                            </AgendaProvider>
                        ) : (
                            <Loading textBlack small paddingTop="2rem" />
                        )}
                    </ScheduleMobileWrapper>
                ) : (
                    <NwCard
                        title="Calendar"
                        icon={byPrefixAndName.fasl['calendar']}
                        fullheight={stretchToParent}
                        footer={
                            !bookingSection && !isAgenda ? (
                                <CalendarFooter
                                    fixedFooter={bookingSection}
                                    type={type}
                                    previewMode={previewMode}
                                    viewCalendar={viewCalendar}
                                    filters={filters}
                                    onSetFilters={setFilters}
                                    onOpenSendAgendaDrawer={onOpenSendAgendaDrawer}
                                    onOpenExportListDrawer={onOpenExportListDrawer}
                                />
                            ) : null
                        }
                        extra={
                            type === 'model' &&
                            !previewMode &&
                            viewCalendar && (
                                <FlexContainer>
                                    <AddEventsButtons onCreateEvent={handleCreateEvent} />
                                </FlexContainer>
                            )
                        }
                        titleAction={
                            viewCalendar &&
                            !bookingSection && (
                                <NwButton
                                    minimal
                                    icon={byPrefixAndName.fass['expand-wide']}
                                    solid
                                    size="small"
                                    onClick={goToModelSchedule}
                                />
                            )
                        }
                        minheight={getCardMinHeight()}
                        padding="reduced"
                    >
                        {viewCalendar ? (
                            <>
                                {!isAgenda ? (
                                    <ModelCalendar
                                        afterReload={handleReloadComplete}
                                        bookingSection={bookingSection}
                                        calendarStyle={calendarStyle}
                                        contactId={contactId}
                                        filters={filters}
                                        onCreateEvent={handleCreateEvent}
                                        onClearContactID={onClearContactID}
                                        onReload={handleReload}
                                        onScheduleRangeChange={onScheduleRangeChange}
                                        params={params}
                                        previewMode={previewMode}
                                        reload={reload}
                                        scheduleRange={scheduleRange}
                                        onOpenSendAgendaDrawer={onOpenSendAgendaDrawerOnSpecificDate}
                                        onOpenAvailableEventsDrawer={onOpenAvailableEventsDrawer}
                                    />
                                ) : scheduleRange ? (
                                    <AgendaProvider values={AgendaContextValues}>
                                        <AgendaContainer stretchToParent={stretchToParent} />
                                        <AgendaFooter
                                            onOpenSendAgendaDrawer={onOpenSendAgendaDrawer}
                                            isContactApproved={isApproved}
                                        />
                                    </AgendaProvider>
                                ) : (
                                    <Loading />
                                )}
                            </>
                        ) : (
                            <LockContainer $previewMode={previewMode} justifyContent="center" alignItems="center">
                                <NwIcon icon={byPrefixAndName.fasl['lock-alt']} />
                            </LockContainer>
                        )}
                        {bookingSection && !isAgenda && (
                            <CalendarFooter
                                fixedFooter={bookingSection}
                                type={type}
                                previewMode={previewMode}
                                viewCalendar={viewCalendar}
                                filters={filters}
                                onSetFilters={setFilters}
                                onOpenSendAgendaDrawer={onOpenSendAgendaDrawer}
                                onOpenExportListDrawer={onOpenExportListDrawer}
                                isContactApproved={isApproved}
                            />
                        )}
                    </NwCard>
                )}
            </ContactScheduleCardWrapper>
            {sendAgenda && (
                <SendAgendaDrawer
                    contactID={contactId}
                    scheduleRange={shareRangeDates}
                    onClose={onCloseSendAgendaDrawer}
                />
            )}
            {exportList && (
                <ExportListDrawer
                    contactID={contactId}
                    scheduleRange={shareRangeDates}
                    onClose={onCloseExportListDrawer}
                />
            )}
            {selectedEvent && (
                <EventDrawer
                    eventType={selectedEvent.TypeName}
                    eventID={selectedEvent.ID}
                    eventObject={selectedEvent}
                    dateSelected={calendarDefaultDate}
                    modelId={type === 'model' && contactId}
                    onClose={handleCloseEventDrawerNoReload}
                    afterUpdate={handleUpdateFromEventDrawer}
                    afterRemove={() => {
                        handleUpdateFromEventDrawer();
                        handleCloseEventDrawer();
                    }}
                />
            )}
            {isEventDrawer && (
                <EventDrawer
                    eventType={newEventType.type}
                    show={true}
                    modelId={contactId}
                    eventInfo={newEventType.eventInfo}
                    viewMode="create"
                    allDay={true}
                    dateSelected={newEventDate ? getMoment(newEventDate) : getNow()}
                    onClose={() => {
                        setIsEventDrawer(false);
                        setNewEventType();
                    }}
                    afterUpdate={() => {
                        setReload(true);
                        handleUpdateFromEventDrawer();
                    }}
                    afterRemove={() => {
                        setIsEventDrawer(false);
                        setReload(true);
                        handleUpdateFromEventDrawer();
                    }}
                />
            )}
            {availableEventsDrawerDate && (
                <AvailableEventsDrawer
                    modelID={contactId}
                    date={availableEventsDrawerDate}
                    onClose={() => setAvailableEventsDrawerDate(null)}
                    afterAddEvent={() => {
                        setReload(true);
                    }}
                />
            )}
        </>
    );
};

export default ContactScheduleCard;
